/**
 * IndexedDB wrapper for offline storage
 */

const DB_NAME = 'MetaxonOfflineDB';
const DB_VERSION = 1;

class OfflineDB {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store for pending signatures (waiting to be uploaded)
        if (!db.objectStoreNames.contains('pendingSignatures')) {
          db.createObjectStore('pendingSignatures', { keyPath: 'id' });
        }

        // Store for offline delivery notes
        if (!db.objectStoreNames.contains('offlineNotes')) {
          db.createObjectStore('offlineNotes', { keyPath: 'id' });
        }

        // Store for offline customers
        if (!db.objectStoreNames.contains('offlineCustomers')) {
          db.createObjectStore('offlineCustomers', { keyPath: 'id' });
        }

        // Store for offline tours
        if (!db.objectStoreNames.contains('offlineTours')) {
          db.createObjectStore('offlineTours', { keyPath: 'id' });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.init();
    }
    return this.db;
  }

  // Pending Signatures
  async addPendingSignature(signatureData) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['pendingSignatures'], 'readwrite');
      const store = tx.objectStore('pendingSignatures');
      const request = store.add(signatureData);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSignatures() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['pendingSignatures'], 'readonly');
      const store = tx.objectStore('pendingSignatures');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePendingSignature(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['pendingSignatures'], 'readwrite');
      const store = tx.objectStore('pendingSignatures');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearPendingSignatures() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['pendingSignatures'], 'readwrite');
      const store = tx.objectStore('pendingSignatures');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Offline Delivery Notes
  async saveNoteForOffline(note) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineNotes'], 'readwrite');
      const store = tx.objectStore('offlineNotes');
      const request = store.put(note);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineNotes() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineNotes'], 'readonly');
      const store = tx.objectStore('offlineNotes');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineNote(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineNotes'], 'readonly');
      const store = tx.objectStore('offlineNotes');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteOfflineNote(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineNotes'], 'readwrite');
      const store = tx.objectStore('offlineNotes');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearOfflineNotes() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineNotes'], 'readwrite');
      const store = tx.objectStore('offlineNotes');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Offline Customers
  async saveCustomerForOffline(customer) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineCustomers'], 'readwrite');
      const store = tx.objectStore('offlineCustomers');
      const request = store.put(customer);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineCustomers() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineCustomers'], 'readonly');
      const store = tx.objectStore('offlineCustomers');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearOfflineCustomers() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineCustomers'], 'readwrite');
      const store = tx.objectStore('offlineCustomers');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Offline Tours
  async saveTourForOffline(tour) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineTours'], 'readwrite');
      const store = tx.objectStore('offlineTours');
      const request = store.put(tour);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineTours() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineTours'], 'readonly');
      const store = tx.objectStore('offlineTours');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineTour(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineTours'], 'readonly');
      const store = tx.objectStore('offlineTours');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteOfflineTour(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineTours'], 'readwrite');
      const store = tx.objectStore('offlineTours');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearOfflineTours() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['offlineTours'], 'readwrite');
      const store = tx.objectStore('offlineTours');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Create singleton instance
const offlineDB = new OfflineDB();

export default offlineDB;